/* @flow */

import * as React from 'react';
import { connect } from 'react-redux';

import type { NETGEM_API_V8_FEED_ITEM } from '../../../libs/netgemLibrary/v8/types/FeedItem';
import type { NETGEM_API_V8_PARENTAL_GUIDANCE } from '../../../libs/netgemLibrary/v8/types/ParentalGuidance';
import type { ModalState } from '../../../redux/modal/reducers';
import type { Dispatch } from '../../../redux/types/types';

import { logInfo, logWarning } from '../../../helpers/debug/debug';
import Localizer from '../../../helpers/localization';
import { Messenger, MessengerEvents } from '../../../helpers/messenger';
import { OtpResponseKind } from '../../../helpers/parentalControl/constsAndTypes';
import { PIN_LENGTH } from '../../../helpers/parentalControl/constsAndTypes';
import ParentalControlManager from '../../../helpers/parentalControl/parentalControlManager';
import ProfilesManager from '../../../helpers/profiles/profilesManager';
import { showPinModal } from '../../../redux/modal/actions';
import ButtonFX from '../../buttons/ButtonFX';
import ButtonLink from '../../buttons/ButtonLink';
import { WidthKind } from '../../buttons/types';
import CodePrompt, { type CodePromptHandle } from '../../codePrompt/CodePrompt';
import { CodeDisplayType } from '../../codePrompt/constsAndTypes';
import Modal, { ModalView } from '../modal';
import './PinModal.css';
import ProfileView from '../profilesModal/Profile';
import { getErrorMessage, getMessageFromStatus, isValidOtp, isValidPinCode } from './helpers';
import {
  type CompletePinModalPropType,
  ErrorSource,
  OTP_LENGTH,
  type PIN_DATA_MODAL_TYPE,
  PinModalKind,
  type PinModalStateType,
  PinModalStep,
  type ReduxPinModalDispatchToPropsType,
} from './PinModalConstsAndTypes';

// Used for error message update timer (in ms)
const ONE_SECOND = 1_000;

class PinModalView extends React.PureComponent<CompletePinModalPropType, PinModalStateType> {
  abortController: AbortController;
  errorMsgTimer: TimeoutID | null;
  modal: ModalView | null;
  otpRef: CodePromptHandle | null;
  pinConfirmationRef: CodePromptHandle | null;
  pinRef: CodePromptHandle | null;

  constructor(props: CompletePinModalPropType) {
    super(props);

    const { pinData } = props;

    const item: NETGEM_API_V8_FEED_ITEM | null = pinData?.item ?? null;
    const parentalGuidance: NETGEM_API_V8_PARENTAL_GUIDANCE | null = pinData?.parentalGuidance ?? null;
    const kind: PinModalKind = pinData?.kind ?? PinModalKind.CheckMaster;

    this.state = {
      currentOtp: '',
      currentPin: '',
      currentPinConfirmation: '',
      errorMsg: '',
      errorMsgAriaLabel: null,
      errorSource: ErrorSource.None,
      isWaitingOtp: false,
      item,
      kind,
      parentalGuidance,
      step: kind === PinModalKind.CreateMaster || kind === PinModalKind.Delete ? PinModalStep.AskOtp : PinModalStep.EnterPin,
    };

    this.abortController = new AbortController();
    this.errorMsgTimer = null;
    this.otpRef = null;
    this.pinConfirmationRef = null;
    this.pinRef = null;
  }

  componentWillUnmount() {
    const { abortController } = this;

    abortController.abort('Component PinModal will unmount');
    this.resetErrorMsgTimer();
  }

  resetErrorMsgTimer: () => void = () => {
    if (this.errorMsgTimer) {
      clearTimeout(this.errorMsgTimer);
    }
  };

  reset: () => void = () => {
    const { kind } = this.state;

    this.otpRef?.clear();
    this.pinRef?.clear();
    this.pinConfirmationRef?.clear();

    this.pinRef?.removeFocus();
    this.pinConfirmationRef?.removeFocus();

    if (kind === PinModalKind.CreateProfile) {
      this.pinRef?.giveFocus();
    } else {
      this.otpRef?.giveFocus();
    }
  };

  handlePinForgottenOnClick: () => void = () => {
    this.resetErrorMsgTimer();
    this.setState({ currentOtp: '', currentPin: '', errorMsg: '', errorMsgAriaLabel: null, errorSource: ErrorSource.None, kind: PinModalKind.CreateMaster, step: PinModalStep.AskOtp });
  };

  updateErrorMessage: (retryAfter: number, tooManyRequests?: boolean, calledByTimer?: boolean) => void = (retryAfter, tooManyRequests, calledByTimer) => {
    const { kind } = this.state;

    const errorMsg = getErrorMessage(kind, retryAfter, tooManyRequests, calledByTimer);

    this.setState((prevState) => {
      return { errorMsg, errorMsgAriaLabel: calledByTimer ? prevState.errorMsgAriaLabel : errorMsg, errorSource: ErrorSource.Pin };
    });

    if (retryAfter > 0) {
      this.resetErrorMsgTimer();

      // Schedule next error message update
      this.errorMsgTimer = setTimeout(() => {
        this.updateErrorMessage(retryAfter - 1, tooManyRequests, true);
      }, ONE_SECOND);
    }
  };

  isPinCodeCorrect: () => Promise<boolean> = async () => {
    const { pinData } = this.props;
    const { currentPin } = this.state;

    if (!isValidPinCode(currentPin)) {
      logInfo(`Invalid PIN code: ${currentPin}`);
      return false;
    }

    // Reset error message
    this.setState({ errorMsg: '', errorMsgAriaLabel: null, errorSource: ErrorSource.None });

    const { isCorrect, retryAfter, tooManyRequests } =
      typeof pinData?.profileId === 'string'
        ? // Check profile PIN
          ProfilesManager.checkPinCode(pinData.profileId, currentPin)
        : // Check admin PIN
          await ParentalControlManager.checkPinCode(currentPin);

    if (isCorrect) {
      return true;
    }

    this.pinRef?.clear();
    this.pinRef?.giveFocus();

    this.updateErrorMessage(retryAfter, tooManyRequests);

    return false;
  };

  unlock: (kind: PinModalKind) => Promise<void> = async (kind) => {
    const { modal } = this;

    if (await this.isPinCodeCorrect()) {
      if (kind === PinModalKind.UnlockContent) {
        Messenger.emit(MessengerEvents.NOTIFY_SUCCESS, Localizer.localize('settings.parental_control.parental_control_disabled_for_session'));
        ParentalControlManager.unlock();
      } else if (kind === PinModalKind.CheckMaster) {
        Messenger.emit(MessengerEvents.NOTIFY_SUCCESS, Localizer.localize('settings.parental_control.valid_master_pin'));
      }

      modal?.close(true);
    }
  };

  checkPinCode: () => Promise<void> = () => this.unlock(PinModalKind.CheckMaster);

  handleAskOtpOnClick: (event: SyntheticMouseEvent<HTMLElement> | SyntheticTouchEvent<HTMLElement>, step: PinModalStep) => Promise<void> = async (event, step) => {
    this.setState({ isWaitingOtp: true });

    await ParentalControlManager.askOtp();
    Messenger.emit(MessengerEvents.NOTIFY_SUCCESS, Localizer.localize('settings.parental_control.otp_sent'));
    this.setState({ isWaitingOtp: false, step });
  };

  handleAlreadyHaveOtpOnClick: (event: SyntheticMouseEvent<HTMLElement> | SyntheticTouchEvent<HTMLElement>, step: PinModalStep) => void = (event, step) => {
    this.setState({ step });
  };

  handleCreateOnClick: () => Promise<void> = async () => {
    const { currentOtp, currentPin } = this.state;
    const { modal } = this;

    if (!isValidOtp(currentOtp) || !isValidPinCode(currentPin)) {
      logInfo('Invalid OTP or PIN');
      return;
    }

    // Reset error message
    this.setState({ errorMsg: '', errorMsgAriaLabel: null, errorSource: ErrorSource.None });

    const status = await ParentalControlManager.createPinCode(currentOtp, currentPin);

    switch (status) {
      case OtpResponseKind.Success:
        Messenger.emit(MessengerEvents.NOTIFY_SUCCESS, Localizer.localize('settings.parental_control.master_pin_created'));

        modal?.close();
        break;

      default:
        this.setState({ errorMsg: getMessageFromStatus(status), errorSource: ErrorSource.Otp });
        this.reset();
    }
  };

  handleCreateProfileOnClick: () => Promise<void> = async () => {
    const { currentPin, currentPinConfirmation } = this.state;
    const { modal } = this;

    if (currentPin === currentPinConfirmation) {
      // User entered twice the same PIN: use it
      this.setState({ errorMsg: '', errorMsgAriaLabel: null, errorSource: ErrorSource.None });
      modal?.close(currentPin);
    } else {
      this.setState({ errorMsg: Localizer.localize('profiles.profile_editor.privacy.pin_mismatch'), errorSource: ErrorSource.Pin });
      this.pinRef?.clear();
      this.pinConfirmationRef?.clear();
      this.pinRef?.giveFocus();
      this.pinConfirmationRef?.removeFocus();
    }
  };

  handleResendOtpOnClick: () => Promise<void> = async () => {
    this.setState({ isWaitingOtp: true });
    await ParentalControlManager.askOtp();
    Messenger.emit(MessengerEvents.NOTIFY_SUCCESS, Localizer.localize('settings.parental_control.otp_sent'));
    this.setState({ isWaitingOtp: false });
  };

  handleDeleteOnClick: () => Promise<void> = async () => {
    const { currentOtp } = this.state;
    const { modal } = this;

    if (!isValidOtp(currentOtp)) {
      logInfo('Invalid OTP');
      return;
    }

    // Reset error message
    this.setState({ errorMsg: '', errorMsgAriaLabel: null, errorSource: ErrorSource.None });

    const status = await ParentalControlManager.deletePinCode(currentOtp);

    switch (status) {
      case OtpResponseKind.Success:
        Messenger.emit(MessengerEvents.NOTIFY_SUCCESS, Localizer.localize('settings.parental_control.master_pin_deleted'));
        modal?.close();
        break;

      default:
        this.setState({ errorMsg: getMessageFromStatus(status), errorSource: ErrorSource.Otp });
        this.reset();
        break;

      // No default
    }
  };

  handleCurrentOtpOnchange: (otp: string, done: boolean) => void = (otp, done) => {
    const { kind } = this.state;

    this.setState({ currentOtp: otp });

    if (done && kind === PinModalKind.CreateMaster) {
      // Deffer focus change
      setTimeout(() => {
        this.otpRef?.removeFocus();
        this.pinRef?.giveFocus();
      }, 0);
    }
  };

  handleCurrentPinOnchange: (pin: string, isComplete: boolean) => void = (pin, isComplete) => {
    const { kind } = this.state;

    this.setState({ currentPin: pin }, async () => {
      if (!isComplete) {
        return;
      }

      if (kind === PinModalKind.CreateMaster || kind === PinModalKind.CreateProfile || kind === PinModalKind.Modify || kind === PinModalKind.Delete) {
        return;
      }

      this.pinRef?.removeFocus();
      this.pinConfirmationRef?.giveFocus();

      switch (kind) {
        case PinModalKind.CheckMaster:
          // Check PIN code
          await this.checkPinCode();
          break;

        case PinModalKind.UnlockAdultContent:
        case PinModalKind.UnlockContent:
        case PinModalKind.UnlockProfile:
        case PinModalKind.UnlockPurchase:
        case PinModalKind.UnlockSettings:
          // Unlock something
          await this.unlock(kind);
          break;

        default:
          // Not supposed to happen
          logWarning(`Unexpect PIN modal kind: ${kind as string}`);
      }
    });
  };

  handleCurrentPinConfirmationOnchange: (pin: string) => void = (pin) => {
    this.setState({ currentPinConfirmation: pin });
  };

  handleOnFocused: (name?: string) => void = (name) => {
    if (name === 'otp') {
      this.pinRef?.removeFocus();
    } else if (name === 'pinConfirmation') {
      this.pinRef?.removeFocus();
    } else {
      this.otpRef?.removeFocus();
      this.pinConfirmationRef?.removeFocus();
    }
  };

  setOtpRef = (instance: CodePromptHandle | null) => {
    this.otpRef = instance;
  };

  setPinRef = (instance: CodePromptHandle | null) => {
    this.pinRef = instance;
  };

  setPinConfirmationRef = (instance: CodePromptHandle | null) => {
    this.pinConfirmationRef = instance;
  };

  renderPinError = (): React.Node => {
    const { errorMsg, errorMsgAriaLabel } = this.state;

    return (
      <div aria-label={errorMsgAriaLabel ?? errorMsg} aria-live='assertive' className='pinError' id='pin-modal-error'>
        {errorMsg}
      </div>
    );
  };

  renderPinCheckEnterPin: (kind: PinModalKind) => React.Node = (kind) => {
    const { pinData } = this.props;
    const { errorMsg } = this.state;

    return (
      <>
        {typeof pinData?.profileId === 'string' ? null : <div>{Localizer.localize('modal.pin.check_master_text')}</div>}
        <CodePrompt
          ariaDescribedBy='pin-modal-error'
          ariaInvalid={Boolean(errorMsg)}
          ariaLabel={Localizer.localize(kind === PinModalKind.CheckMaster ? 'accessibility.modal.enter_master_pin' : 'accessibility.modal.enter_profile_pin')}
          displayType={CodeDisplayType.Hidden}
          hasFocus
          length={PIN_LENGTH}
          onChange={this.handleCurrentPinOnchange}
          ref={this.setPinRef}
        />
        {this.renderPinError()}
        <ButtonLink onClick={this.handlePinForgottenOnClick}>{Localizer.localize('settings.parental_control.forgotten_master_pin')}</ButtonLink>
      </>
    );
  };

  renderPinCheck: (kind: PinModalKind) => React.Node = (kind) => {
    const { step } = this.state;

    if (step === PinModalStep.EnterPin) {
      return this.renderPinCheckEnterPin(kind);
    }

    return null;
  };

  renderPinUnlockContent: () => React.Node = () => {
    const { errorMsg, item, parentalGuidance } = this.state;

    let pgLabel = null;
    if (item) {
      pgLabel = ParentalControlManager.getLabelElementFromItem(item);
    } else if (parentalGuidance) {
      pgLabel = ParentalControlManager.getLabelElementFromParentalGuidance(parentalGuidance);
    }

    return (
      <>
        <div>{Localizer.localize('modal.pin.unlock_text_video')}</div>
        <CodePrompt
          ariaDescribedBy='pin-modal-error'
          ariaInvalid={Boolean(errorMsg)}
          ariaLabel={Localizer.localize('accessibility.modal.enter_master_pin')}
          displayType={CodeDisplayType.Hidden}
          hasFocus
          length={PIN_LENGTH}
          onChange={this.handleCurrentPinOnchange}
          ref={this.setPinRef}
        />
        {this.renderPinError()}
        {pgLabel}
        <div className='instructions'>{ParentalControlManager.getTextForCurrentMaxAgeAllowedForVideo()}</div>
        <ButtonLink onClick={this.handlePinForgottenOnClick}>{Localizer.localize('settings.parental_control.forgotten_master_pin')}</ButtonLink>
      </>
    );
  };

  renderPinUnlockPurchase: () => React.Node = () => {
    const { errorMsg } = this.state;

    return (
      <>
        <div>{Localizer.localize('settings.parental_control.enter_master_pin_to_unlock_purchase')}</div>
        <CodePrompt
          ariaDescribedBy='pin-modal-error'
          ariaInvalid={Boolean(errorMsg)}
          ariaLabel={Localizer.localize('accessibility.modal.enter_master_pin')}
          displayType={CodeDisplayType.Hidden}
          hasFocus
          length={PIN_LENGTH}
          onChange={this.handleCurrentPinOnchange}
          ref={this.setPinRef}
        />
        {this.renderPinError()}
        <ButtonLink onClick={this.handlePinForgottenOnClick}>{Localizer.localize('settings.parental_control.forgotten_master_pin')}</ButtonLink>
      </>
    );
  };

  renderPinUnlockSettings: () => React.Node = () => {
    const { errorMsg } = this.state;

    return (
      <>
        <div>{Localizer.localize('settings.parental_control.enter_master_pin_to_unlock_settings')}</div>
        <CodePrompt
          ariaDescribedBy='pin-modal-error'
          ariaInvalid={Boolean(errorMsg)}
          ariaLabel={Localizer.localize('accessibility.modal.enter_master_pin')}
          displayType={CodeDisplayType.Hidden}
          hasFocus
          length={PIN_LENGTH}
          onChange={this.handleCurrentPinOnchange}
          ref={this.setPinRef}
        />
        {this.renderPinError()}
        <ButtonLink onClick={this.handlePinForgottenOnClick}>{Localizer.localize('settings.parental_control.forgotten_master_pin')}</ButtonLink>
      </>
    );
  };

  renderPinAskOtp: (key: string, nextStep: PinModalStep) => React.Node = (key, nextStep) => {
    const { isWaitingOtp } = this.state;

    return (
      <>
        <div>{Localizer.localize(key)}</div>
        <div>{Localizer.localize('settings.parental_control.click_button_to_receive_otp')}</div>
        <ButtonFX autoFocus data={nextStep} isLoading={isWaitingOtp} onClick={this.handleAskOtpOnClick} widthKind={WidthKind.Stretched}>
          {Localizer.localize('settings.parental_control.otp_button')}
        </ButtonFX>
        <ButtonLink data={PinModalStep.EnterOtpAndPin} onClick={this.handleAlreadyHaveOtpOnClick}>
          {Localizer.localize('settings.parental_control.already_have_otp')}
        </ButtonLink>
      </>
    );
  };

  renderPinCreateAskOtp: () => React.Node = () => this.renderPinAskOtp('settings.parental_control.otp_required_to_create_master_pin', PinModalStep.EnterOtpAndPin);

  renderPinCreateEnterOtpAndPin: () => React.Node = () => {
    const { currentOtp, currentPin, errorMsg, errorSource, isWaitingOtp } = this.state;

    return (
      <>
        <div>{Localizer.localize('settings.parental_control.otp_prompt')}</div>
        <CodePrompt
          allowPaste
          ariaDescribedBy={errorMsg && errorSource === ErrorSource.Otp ? 'pin-modal-error' : undefined}
          ariaInvalid={Boolean(errorMsg)}
          ariaLabel={Localizer.localize('accessibility.modal.enter_otp')}
          displayType={CodeDisplayType.Visible}
          hasFocus
          length={OTP_LENGTH}
          name='otp'
          onChange={this.handleCurrentOtpOnchange}
          onFocused={this.handleOnFocused}
          ref={this.setOtpRef}
        />
        <hr />
        <div>{Localizer.localize('settings.parental_control.master_pin_creation_prompt')}</div>
        <CodePrompt
          ariaDescribedBy={errorMsg && errorSource === ErrorSource.Pin ? 'pin-modal-error' : undefined}
          ariaInvalid={Boolean(errorMsg)}
          ariaLabel={Localizer.localize('accessibility.modal.enter_master_pin')}
          displayType={CodeDisplayType.Hidden}
          hasFocus={false}
          length={PIN_LENGTH}
          name='pin'
          onChange={this.handleCurrentPinOnchange}
          onFocused={this.handleOnFocused}
          ref={this.setPinRef}
        />
        {this.renderPinError()}
        <ButtonFX isDisabled={!isValidPinCode(currentPin) || !isValidOtp(currentOtp)} onClick={this.handleCreateOnClick} widthKind={WidthKind.Stretched}>
          {Localizer.localize('modal.pin.create')}
        </ButtonFX>
        <ButtonLink isDisabled={isWaitingOtp} onClick={this.handleResendOtpOnClick}>
          {Localizer.localize('settings.parental_control.resend_otp')}
        </ButtonLink>
      </>
    );
  };

  renderPinCreate: () => React.Node = () => {
    const { step } = this.state;

    switch (step) {
      case PinModalStep.AskOtp:
        return this.renderPinCreateAskOtp();
      case PinModalStep.EnterOtpAndPin:
        return this.renderPinCreateEnterOtpAndPin();

      default:
        return null;
    }
  };

  renderPinCreateProfile: () => React.Node = () => {
    const { currentPin, currentPinConfirmation, errorMsg, errorSource } = this.state;

    return (
      <>
        <div>{Localizer.localize('profiles.profile_editor.privacy.enter_profile_pin')}</div>
        <CodePrompt
          ariaDescribedBy={errorMsg && errorSource === ErrorSource.Otp ? 'pin-modal-error' : undefined}
          ariaInvalid={Boolean(errorMsg)}
          ariaLabel={Localizer.localize('accessibility.modal.enter_profile_pin')}
          displayType={CodeDisplayType.Hidden}
          hasFocus
          length={PIN_LENGTH}
          name='pin'
          onChange={this.handleCurrentPinOnchange}
          onFocused={this.handleOnFocused}
          ref={this.setPinRef}
        />
        <hr />
        <div>{Localizer.localize('profiles.profile_editor.privacy.enter_profile_pin_confirmation')}</div>
        <CodePrompt
          ariaDescribedBy={errorMsg && errorSource === ErrorSource.Pin ? 'pin-modal-error' : undefined}
          ariaInvalid={Boolean(errorMsg)}
          ariaLabel={Localizer.localize('accessibility.modal.enter_profile_pin')}
          displayType={CodeDisplayType.Hidden}
          hasFocus={false}
          length={PIN_LENGTH}
          name='pinConfirmation'
          onChange={this.handleCurrentPinConfirmationOnchange}
          onFocused={this.handleOnFocused}
          ref={this.setPinConfirmationRef}
        />
        {this.renderPinError()}
        <ButtonFX isDisabled={!isValidPinCode(currentPin) || !isValidPinCode(currentPinConfirmation)} onClick={this.handleCreateProfileOnClick} widthKind={WidthKind.Stretched}>
          {Localizer.localize('common.actions.ok')}
        </ButtonFX>
      </>
    );
  };

  renderPinDeleteAskOtp: () => React.Node = () => this.renderPinAskOtp('settings.parental_control.otp_required_to_delete_master_pin', PinModalStep.EnterOtp);

  renderPinDeleteEnterOtp: () => React.Node = () => {
    const { currentOtp, errorMsg } = this.state;

    return (
      <>
        <div>{Localizer.localize('settings.parental_control.enter_otp')}</div>
        <CodePrompt
          allowPaste
          ariaDescribedBy='pin-modal-error'
          ariaInvalid={Boolean(errorMsg)}
          ariaLabel={Localizer.localize('accessibility.modal.enter_otp')}
          displayType={CodeDisplayType.Visible}
          hasFocus
          length={OTP_LENGTH}
          name='otp'
          onChange={this.handleCurrentOtpOnchange}
          onFocused={this.handleOnFocused}
          ref={this.setOtpRef}
        />
        {this.renderPinError()}
        <ButtonFX isDisabled={!isValidOtp(currentOtp)} onClick={this.handleDeleteOnClick} widthKind={WidthKind.Stretched}>
          {Localizer.localize('modal.pin.delete')}
        </ButtonFX>
        <ButtonLink onClick={this.handleResendOtpOnClick}>{Localizer.localize('settings.parental_control.resend_otp')}</ButtonLink>
      </>
    );
  };

  renderPinDelete: () => React.Node = () => {
    const { step } = this.state;

    if (step === PinModalStep.AskOtp) {
      return this.renderPinDeleteAskOtp();
    }

    if (step === PinModalStep.EnterOtp) {
      return this.renderPinDeleteEnterOtp();
    }

    return null;
  };

  getModalTitle: (kind: PinModalKind) => string = (kind) => {
    switch (kind) {
      case PinModalKind.CreateMaster:
        return 'modal.pin.create_master_pin_title';
      case PinModalKind.CreateProfile:
        return 'modal.pin.create_profile_pin_title';
      case PinModalKind.Delete:
        return 'modal.pin.delete_title';
      case PinModalKind.Modify:
        return 'modal.pin.modify_title';
      case PinModalKind.UnlockAdultContent:
      case PinModalKind.UnlockContent:
        return 'modal.pin.unlock_title';
      case PinModalKind.UnlockProfile:
        return '';
      case PinModalKind.CheckMaster:
      default:
        return 'modal.pin.check_master_title';
    }
  };

  getModalContent: (kind: PinModalKind) => React.Node = (kind) => {
    switch (kind) {
      case PinModalKind.CheckMaster:
      case PinModalKind.UnlockProfile:
        return this.renderPinCheck(kind);
      case PinModalKind.CreateMaster:
        return this.renderPinCreate();
      case PinModalKind.CreateProfile:
      case PinModalKind.Modify:
        return this.renderPinCreateProfile();
      case PinModalKind.Delete:
        return this.renderPinDelete();
      case PinModalKind.UnlockAdultContent:
      case PinModalKind.UnlockContent:
        return this.renderPinUnlockContent();
      case PinModalKind.UnlockPurchase:
        return this.renderPinUnlockPurchase();
      case PinModalKind.UnlockSettings:
        return this.renderPinUnlockSettings();

      // No default
    }
  };

  setModalRef = (instance: ModalView | null) => {
    this.modal = instance;
  };

  getAriaLabelKey = (): string => {
    const { kind } = this.state;

    switch (kind) {
      case PinModalKind.CheckMaster:
        return 'accessibility.modal.master_pin_check';
      case PinModalKind.CreateMaster:
        return 'accessibility.modal.master_pin_create';
      case PinModalKind.CreateProfile:
        return 'accessibility.modal.profile_pin_create';
      case PinModalKind.Delete:
        return 'accessibility.modal.master_pin_delete';
      case PinModalKind.Modify:
        return 'accessibility.modal.master_pin_modify';
      case PinModalKind.UnlockAdultContent:
      case PinModalKind.UnlockContent:
        return 'accessibility.modal.master_pin_unlock_content';
      case PinModalKind.UnlockProfile:
        return 'accessibility.modal.profile_pin_unlock_profile';
      case PinModalKind.UnlockPurchase:
        return 'accessibility.modal.master_pin_unlock_purchase';
      case PinModalKind.UnlockSettings:
        return 'accessibility.modal.master_pin_unlock_settings';

      // No default
    }
  };

  render(): React.Node {
    const { modalId, pinData } = this.props;
    const { kind } = this.state;

    const headerKey = this.getModalTitle(kind);
    const name = typeof pinData?.profileId === 'string' ? ProfilesManager.getProfile(pinData.profileId).name : '';

    return (
      <Modal
        ariaLabel={Localizer.localize(this.getAriaLabelKey(), { name })}
        canCloseOnOverlayClick={kind !== PinModalKind.CreateMaster && kind !== PinModalKind.Delete}
        className='pin'
        header={headerKey ? Localizer.localize(headerKey) : ''}
        innerRef={this.setModalRef}
        modalId={modalId}
      >
        {typeof pinData?.profileId === 'string' ? (
          <>
            <ProfileView profile={ProfilesManager.getProfile(pinData.profileId)} />
            <div className='pinHint'>{Localizer.localize('settings.parental_control.private_profile_hint')}</div>
          </>
        ) : null}
        <div className='pinContent'>{this.getModalContent(kind)}</div>
      </Modal>
    );
  }
}

const mapDispatchToProps = (dispatch: Dispatch): ReduxPinModalDispatchToPropsType => {
  return {
    localShowPinModal: (data: PIN_DATA_MODAL_TYPE): void => dispatch(showPinModal(data)),
  };
};

const PinModal: React.ComponentType<ModalState> = connect(null, mapDispatchToProps)(PinModalView);

export default PinModal;
